import {apiAction, apiActionImproved, apiRetrieveImproved} from "../api.ts";
import type {BookingClubsResponse, CancelPreview, StartTimesResponse} from "../bookings/types";
import type {BookingSettings, CalendarDay, CalendarWeek, PricePreview} from "../bookings/organiser/types";
import {ClubSubscriptionTierType} from "../constants.ts";
import {AiInsight, Americano, AmericanoLeaderboardEntry, AmericanoGame, AmericanoPlayerStats, Mexicano, MexicanoLeaderboardEntry, MexicanoGame, MexicanoPlayerStats, Assessment, Booking, ClubCustomReward, ClubGroup, ClubGroupMember, CoinsStatsResponse, Conversation, Court, CourtAvailabilityResponse, FirebaseMessage, KotcLeaderboard, League, LeagueParticipant, Message, MonthlyAvailabilityResponse, Player, Review, RunningGetResultsResponse, TournamentRoundup} from "../types";

/** What a court slot would cost, and how it would be paid — the server's answer, never the client's. */
export interface BookingQuote {
    amount_cents: number;
    currency_code: string | null;
    priced: boolean;
    pay_at_club: boolean;
    peak_minutes: number;
    off_peak_minutes: number;
    share_cents: number;
    max_players: number;
    error?: string;
}

export interface BookingStartParams {
    court_uid: string;
    start_date: any;
    start_time: string | null;
    duration_minutes: number;
    phone_number?: string;
    mode?: 'pay_part' | 'pay_everything';
    max_players?: number;
    invitees?: string[];
}

/** booking-book: either a Checkout URL to pay the hold, or a booking confirmed as pay-at-club. */
export interface BookingStartResponse {
    success?: boolean;
    booking_uid: string;
    status: number;
    pay_at_club: boolean;
    amount_cents: number;
    currency_code: string | null;
    hold_expires_at: string | null;
    redirect_url: string | null;
    error?: string;
}

export interface PricingFeesResponse {
    default_country: string;
    schedule: Record<string, {
        rate: number;
        fixed_cents: number;
        currency: string;
        // `club` is the $39 Plus tier — the wire key predates the current plan names.
        plan_rates: { payg: number; starter: number; club: number; pro: number };
    }>;
    plans: Array<{ key: string; label: string; tier: number; monthly: number; yearly: number; rate: number; fixed_cents: number }>;
    // What a new organiser pays under payments v3 (AllInFees, set from Master), same shape as `schedule`,
    // only for countries whose currency has a row. `schedule` is the pre-v3 (standard account) one.
    payments_v3?: { enabled: boolean; schedule: PricingFeesResponse['schedule'] };
}

/**
 * The one-off path's size-tiered Event Pass quote for ONE tournament — from the server's single
 * resolver, so whatever renders it shows exactly what Stripe will charge. `topup` means the
 * event already holds a smaller band and `amount_cents` is the difference.
 */
export interface OneOffSizedQuote {
    kind?: 'sized';
    amount_cents: number;
    list_cents: number;
    paid_cents: number;
    currency: string;
    entries: number;
    entries_covered: number | null;
    band_label: string;
    topup: boolean;
    earn?: OneOffEarnPicture;
}

/** A one-off event quiz signup: the organizer's token and the event built from their list. */
export interface EventQuizSignupResponse {
    access_token: string;
    user: any;
    event: {
        kind: 'tournament' | 'league';
        type: string;
        uid: string;
        title: string;
        public_url: string;
        manage_path: string;
        entries: number;
        draw_created: boolean;
        entry_fee: {amount: number; currency: string} | null;
        dupr: boolean;
        community: {name: string; join_url: string} | null;
    };
    invites: {queued: number; whatsapp: number; email: number};
}

/** The roster-derived earn route: people with a verified phone vs the ceil-of-share target. */
export interface OneOffEarnPicture {
    applies: boolean;
    eligible: boolean;
    entrants: number;
    claimed: number;
    required: number;
    remaining: number;
    met: boolean;
    min_entrants: number;
    join_link?: string | null;
    entrants_detail?: {uid: string; name: string; claimed: boolean; claimable: boolean}[];
}

/** club-player-claims: one player on the profile players tile. */
export interface ClubPlayerClaim {
    uid: string;
    version: number;
    name: string;
    first: string | null;
    phone: string | null;
    has_email: boolean;
    claimed: boolean;
    /** The personal claim link, for a player still to register; null once registered. */
    claim_url: string | null;
    /** When ScorePal last texted this player for the club (ISO-ish server time), if ever. */
    last_texted_at: string | null;
    texts_sent: number;
    /** Why ScorePal cannot text them right now (no mobile, cooldown, cap…); null when it can. */
    text_blocked: string | null;
}

export interface ClubPlayerClaims {
    club_name: string | null;
    total: number;
    claimed: number;
    unclaimed: number;
    /** Unregistered players with a mobile on file — the ones a text can reach. */
    with_phone: number;
    /** ScorePal can send the invite texts itself (Twilio SMS configured on the server). */
    sms_enabled: boolean;
    /** How many players ScorePal could text right now. */
    textable: number;
    /** What is left of the club's daily text allowance. */
    texts_left: number;
    players: ClubPlayerClaim[];
}

export interface ClubInviteSendResult {
    sent: number;
    failed: number;
    /** Still waiting after this batch — call again. */
    remaining: number;
    /** Stopped by the daily allowance with players still waiting. */
    capped: boolean;
    results: {uid: string; name: string; ok: boolean; error: string | null}[];
}

/** event-pass/go-live-status: what the event pages show before any gate. */
/** One server fact for the banner, the modal and the cards (OneOffPathService::passState). */
/** /build/'s facts after a load (build/config). */
export interface BuildConfig {
    enabled: boolean;
    pricing: import('../sports/registry.ts').PricingFacts;
    routes: string[];
    access: string[];
    max_entries: number;
    min_entries: number;
    provisional_days: number;
}

/** /build/'s draw step: the draw, the field and the links for the token's provisional event. */
export interface BuildPicture {
    uid: string;
    route: 'tournament' | 'league' | 'session' | 'social';
    access: 'add' | 'link' | 'pay';
    format: string | null;
    formats: string[];
    kind: 'tournament' | 'league';
    claimed: boolean;
    expires_at: string | null;
    event: {uid: string; title: string; slug: string | null; sport_type: string};
    entries: number;
    unit: 'teams' | 'players';
    field: string[];
    /** The field with its numbered placeholders marked; joiners take them over in place. */
    field_items: {name: string; placeholder: boolean}[];
    open_slots: number;
    sections: {title: string; rounds: {title: string; matches: {a: string; b: string; court: number | null; date: string | null}[]}[]}[];
    registrations: number;
    links: {public: string; register: string | null; whatsapp_code: string | null; whatsapp_url: string | null; admin: string | null; message: string};
}

export interface PassState {
    state: 'free' | 'required' | 'topup_due' | 'active' | 'covered';
    pass_kind: string | null;
    covered_by: 'legacy' | 'annual_pass' | 'plus' | 'pro' | string | null;
    entries: number;
    unit: 'teams' | 'players';
    cap: number;
    held_pass: boolean;
    /** The held pass "Use your pass" would put on (smallest top-up, then highest tier) and what it still owes. */
    held_pass_tier?: number | null;
    held_pass_label?: string | null;
    held_pass_topup_cents?: number;
    event_tier?: number;
    /** A pass on the event that covers less than the field: `topup_due` until `topup_due_cents` is paid. */
    attached_pass_tier?: number | null;
    topup_due_cents?: number;
    /** `required` only: the free results before the pass — how many, how many taken. */
    free_scores?: FreeScores | null;
}

/** Two free results before the pass (FreeScores): the allowance and what this event has used of it. */
export interface FreeScores {
    allowed: number;
    used: number;
    left: number;
}

/** The $2.99 Plus trial as the server pictures it (PlusTrial::picture): eligibility, the running trial, the facts. */
export interface PlusTrialPicture {
    enabled: boolean;
    eligible: boolean;
    reason: 'off' | 'on_plan' | 'club_used' | 'mailbox_used' | null;
    active: boolean;
    cancelled: boolean;
    ends_at: string | null;
    converted: boolean;
    days_left: number;
    days: number;
    fee_cents: number;
    primary: boolean;
    monthly_cents: number;
}

export interface OneOffGoLiveStatus {
    applies: boolean;
    pass_state?: PassState;
    /** An unused pass in the organizer's wallet: "Use your pass". */
    held_pass?: boolean;
    /** The go-live gate (golive_gate_enabled): the field in the cap's unit, and the cap. */
    gate_on?: boolean;
    entry_count?: {entries: number; unit: 'teams' | 'players'};
    cap?: number;
    /** The gate payload (code event_pass_required) when the event needs a pass, else null. */
    offer: (Record<string, any> & {code: string; amount_cents: number; currency: string}) | null;
    has_pass: boolean;
    pass: {kind: string; entries_covered: number | null; earned: boolean; used_at: string | null} | null;
    counted_entries: number | null;
    covered_entries: number | null;
    live: boolean;
    /** Model2Features keys this event's pass grants (empty without a pass). */
    pass_features: string[];
}

const services = {
    core:          {
        sponsorsUpload: function (formData: any) {
            return apiAction('sponsors-upload', formData, {
                body: formData,
            });
        },
        async bannerCrop(image_url: string, crop: {x: number; y: number; width: number; height: number}): Promise<{banner_url: string}> {
            return apiActionImproved('banner-crop', {image_url, crop})
        },
        saveVenue:      function (venue: string) {
            return apiActionImproved('save-venue' as any, {venue}, {method: "POST"})
        },
        deleteVenue:    function (venue: string) {
            return apiActionImproved('delete-venue' as any, {venue}, {method: "POST"})
        },
    },
    admin:         {
        audits: function (formData: any) {
            return apiActionImproved('master/audits' as any, formData, {
                body: formData,
            });
        },
        transactions: function (formData: any) {
            return apiActionImproved('master/transactions' as any, formData, {
                body: formData,
            });
        },
        products: function (formData: any) {
            return apiActionImproved('master/products' as any, formData, {
                body: formData,
            });
        },
        createPriceOnStripe: function (price_uid: string) {
            return apiActionImproved(`master/products/create-price-on-stripe/${price_uid}` as any);
        },
    },
    aiInsights:    {
        async index(): Promise<AiInsight[]> {
            return apiRetrieveImproved('ai-insights' as any, {}, {method: 'GET', body: undefined})
        },
        async show(ai_uid: string): Promise<AiInsight> {
            return apiRetrieveImproved(`ai-insights/${ai_uid}` as any, {}, {method: 'GET', body: undefined})
        },
        async store(data: Partial<AiInsight>): Promise<AiInsight> {
            return apiActionImproved('ai-insights' as any, data, {method: "POST"})
        },
        async update(data: Partial<AiInsight>): Promise<any> {
            return apiActionImproved(`ai-insights/${data.uid}` as any, {values: data}, {method: "PATCH"})
        },
        async destroy(data: Partial<AiInsight>): Promise<AiInsight[]> {
            return apiActionImproved(`ai-insights/${data.uid}` as any, data, {method: "DELETE"})
        },
        async test(uid: string, data: any): Promise<{ status: string, message: string }> {
            return apiActionImproved(`ai-insights/${uid}/test` as any, data, {method: "POST"})
        },
        async getResult(league_uid: string, ai_insight_uid: string): Promise<any> {
            const queryParams = new URLSearchParams({
                league_uid,
                ai_insight_uid
            }).toString();

            return apiRetrieveImproved(`ai-insights-results-get?${queryParams}` as any, {}, {method: 'GET', body: undefined})
        },
        async calculateGameProbability(game_uid: string): Promise<any> {
            return apiActionImproved('game-probability-calculate' as any, {game_uid}, {method: "POST"})
        },
        async calculatePlayerInsights(player_uid: string, player_name?: string): Promise<any> {
            return apiActionImproved('player-insights-calculate' as any, {player_uid, player_name}, {method: "POST"})
        },
        async getPlayerInsightsResult(player_uid: string): Promise<any> {
            return apiActionImproved('player-insights-result' as any, {player_uid}, {method: "POST"})
        },
    },
    firebase:      {
        async messages(): Promise<FirebaseMessage[]> {
            return apiRetrieveImproved('firebase-messages' as any, {}, {method: 'GET', body: undefined})
        },
        async sendTestMessage(test_msg: any): Promise<[]> {
            return apiActionImproved('firebase-send-test-message' as any, test_msg, {method: "POST"})
        },
    },
    ladder:        {
        async reset(league_uid: string): Promise<{ games_deleted: number }> {
            return apiActionImproved('league-ladder-reset' as any, {league_uid}, {method: 'POST'})
        },
        async setInitialPositions(league_uid: string, positions: { uid: string; position: number }[]): Promise<{ message: string }> {
            return apiActionImproved(`leagues/${league_uid}/set-positions` as any, {positions}, {method: 'POST'})
        },
        async recalculate(league_uid: string): Promise<{ message: string; participants: LeagueParticipant[] }> {
            return apiActionImproved(`leagues/${league_uid}/recalculate` as any, {}, {method: 'POST'})
        },
    },
    profile:       {
        // Emails the claim link to the profile's own address; logs nobody in.
        async claim(player_uid: string): Promise<{ success: boolean; claim_link_sent: boolean; email_hint?: string }> {
            return apiActionImproved('profile-claim', {player_uid})
        },
        async resendVerification(): Promise<any> {
            return apiActionImproved('confirm-email-resend' as any, {})
        },
        async unclaim(player_uid: string): Promise<{ success: boolean; user: Player }> {
            return apiActionImproved('profile-unclaim' as any, {player_uid})
        },
    },
    players:       {
        async create(): Promise<Player> {
            return apiActionImproved('players-create', {})
        },
        /** The profile players tile: every player the club has, who has registered, and a claim link for the rest. */
        async clubClaims(club_uid: string): Promise<ClubPlayerClaims> {
            return apiActionImproved('club-player-claims' as any, {club_uid})
        },
        /** ScorePal texts the claim link (Twilio). One batch per call — loop while `remaining` > 0. */
        async sendInvites(club_uid: string, who: {all: true} | {player_uids: string[]}): Promise<ClubInviteSendResult> {
            return apiActionImproved('club-player-invites-send' as any, {club_uid, ...who})
        },
        /** The short link in an invite text → the account and its claim token. */
        async resolveInvite(code: string): Promise<{uid: string; token: string; email: string | null}> {
            return apiActionImproved('invite-resolve' as any, {code})
        },
        /** Save a mobile on a player the organizer added (the same players-update the Players tab uses). */
        async setPhone(uid: string, version: number, phone_number: string): Promise<any> {
            return apiActionImproved('players-update', {uid, values: {version: version + 1, phone_number}})
        },
        async createAndAdd(data: { first_name: string; last_name?: string; display_name?: string; email?: string; phone?: string; league_uid?: string; tournament_uid?: string; division?: string; dedupe_by_name?: boolean }): Promise<Player> {
            return apiActionImproved('players-create-and-add', data)
        },
        async delete(uid: string): Promise<any> {
            return apiActionImproved('players-delete', {uid})
        },
        async search(data: { searchText?: string; club_uid?: string; league_uid?: string; event_partner_tournament_uid?: string; event_partner_league_uid?: string; role?: number; selected_key?: string; is_one_off?: boolean; coaches_only?: boolean }): Promise<Player[]> {
            return apiActionImproved('players-search', data)
        },
    },
    games:         {
        async get(params: Record<string, any>): Promise<any> {
            return apiRetrieveImproved('games-get', params)
        },
        async update(uid: string, values: any): Promise<any> {
            return apiActionImproved('games-update', {uid, values})
        },
    },
    leagueOfLeagues: {
        async get(filters?: { sport_type?: string; country_code?: string; city?: string; period_type?: string; period_value?: string }): Promise<any> {
            const params = new URLSearchParams();
            if (filters?.sport_type) params.set('sport_type', filters.sport_type);
            if (filters?.country_code) params.set('country_code', filters.country_code);
            if (filters?.city) params.set('city', filters.city);
            if (filters?.period_type) params.set('period_type', filters.period_type);
            if (filters?.period_value) params.set('period_value', filters.period_value);
            const qs = params.toString();
            return apiRetrieveImproved(('league-of-leagues' + (qs ? '?' + qs : '')) as any, {}, {method: 'GET', body: null})
        },
    },
    playerLeaderboard: {
        async get(filters?: { sport_type?: string; country_code?: string; city?: string; page?: number; per_page?: number; period_type?: string; period_value?: string }): Promise<any> {
            const params = new URLSearchParams();
            if (filters?.sport_type) params.set('sport_type', filters.sport_type);
            if (filters?.country_code) params.set('country_code', filters.country_code);
            if (filters?.city) params.set('city', filters.city);
            if (filters?.page) params.set('page', String(filters.page));
            if (filters?.per_page) params.set('per_page', String(filters.per_page));
            if (filters?.period_type) params.set('period_type', filters.period_type);
            if (filters?.period_value) params.set('period_value', filters.period_value);
            const qs = params.toString();
            return apiRetrieveImproved(('player-leaderboard' + (qs ? '?' + qs : '')) as any, {}, {method: 'GET', body: null})
        },
    },
    // Organiser League — organizers ranked by badge coins; same response shape as playerLeaderboard.
    organizerLeaderboard: {
        async get(filters?: { country_code?: string; page?: number; per_page?: number; period_type?: string; period_value?: string }): Promise<any> {
            const params = new URLSearchParams();
            if (filters?.country_code) params.set('country_code', filters.country_code);
            if (filters?.page) params.set('page', String(filters.page));
            if (filters?.per_page) params.set('per_page', String(filters.per_page));
            if (filters?.period_type) params.set('period_type', filters.period_type);
            if (filters?.period_value) params.set('period_value', filters.period_value);
            const qs = params.toString();
            return apiRetrieveImproved(('organizer-leaderboard' + (qs ? '?' + qs : '')) as any, {}, {method: 'GET', body: null})
        },
    },
    clubLeaderboard: {
        async get(filters: { club_uid: string; page?: number; per_page?: number; period_type?: string; period_value?: string }): Promise<any> {
            const params = new URLSearchParams();
            params.set('club_uid', filters.club_uid);
            if (filters.page) params.set('page', String(filters.page));
            if (filters.per_page) params.set('per_page', String(filters.per_page));
            if (filters.period_type) params.set('period_type', filters.period_type);
            if (filters.period_value) params.set('period_value', filters.period_value);
            return apiRetrieveImproved(('club-leaderboard?' + params.toString()) as any, {}, {method: 'GET', body: null})
        },
    },
    playerCoinSignal: {
        async get(): Promise<any> {
            return apiRetrieveImproved('player-coin-signal' as any, {}, {method: 'GET', body: null})
        },
    },
    organizerLolSignal: {
        async get(): Promise<any> {
            return apiRetrieveImproved('organizer-lol-signal' as any, {}, {method: 'GET', body: null})
        },
    },
    prizePool: {
        async get(): Promise<{prize_pool: number}> {
            return apiRetrieveImproved('coins-prize-pool' as any, {}, {method: 'GET', body: null})
        },
    },
    leaderboardEnrollment: {
        async enroll(): Promise<{success: boolean; enrolled: boolean}> {
            return apiActionImproved('leaderboard-enroll' as any, {})
        },
        async optOut(): Promise<{success: boolean; enrolled: boolean}> {
            return apiActionImproved('leaderboard-opt-out' as any, {})
        },
        async status(): Promise<{enrolled: boolean; opted_out: boolean; enrolled_at: string | null; opted_out_at: string | null}> {
            return apiRetrieveImproved('leaderboard-enrollment-status' as any, {}, {method: 'GET', body: null})
        },
    },
    leagues:       {
        async find(league_uid: string): Promise<any> {
            return apiRetrieveImproved('leagues-find', {uid: league_uid})
        },
        async update(uid: string, values: any): Promise<any> {
            return apiActionImproved('leagues-update', {uid, values})
        },
        async createSeason(data: Record<string, any>): Promise<any> {
            return apiActionImproved('leagues-season-create', data, {method: 'POST'})
        },
        async seasonPreview(season_uid: string): Promise<any> {
            return apiActionImproved('leagues-season-preview', {season_uid}, {method: 'POST'})
        },
        async seasonCreateNext(season_uid: string, overrides?: any[]): Promise<any> {
            return apiActionImproved('leagues-season-create-next', {season_uid, overrides}, {method: 'POST'})
        },
        async stats(league_uid: string, month_key?: string): Promise<any> {
            return apiRetrieveImproved('leagues-stats', {league_uid, month_key})
        },
        async seasonStats(season_uid: string): Promise<any> {
            return apiRetrieveImproved('leagues-season-stats', {season_uid})
        },
        async games(league_uid: string): Promise<any> {
            return apiRetrieveImproved('games-get', {league_uid})
        },
        async players(league_uid: string): Promise<any> {
            return apiRetrieveImproved('players-get', {league_uid})
        },
        async participants(league_uid: string, order_by_position?: boolean): Promise<any> {
            return apiRetrieveImproved('league-participants-get', {league_uid, order_by_position})
        },
        async invite(league_uid: string, invited_player_uid: string): Promise<{ application_uid: string }> {
            return apiActionImproved('leagues-invite' as any, {league_uid, invited_player_uid})
        },
        async applications(league_uid: string): Promise<any[]> {
            return apiRetrieveImproved('applications-get' as any, {league_uid})
        },
        async approveApplication(uid: string, version: number): Promise<any> {
            return apiActionImproved('applications-update', {uid, values: {status: 2, version}})
        },
        async teams(league_uid: string): Promise<any[]> {
            return apiRetrieveImproved('leagues-teams' as any, {league_uid})
        },
        async createTeam(league_uid: string, p1_uid: string, p2_uid: string): Promise<any> {
            return apiActionImproved('teams-create', {league_uid, p1_uid, p2_uid})
        },
        async unassignedPlayers(league_uid: string): Promise<any[]> {
            return apiRetrieveImproved('leagues-players-unassigned' as any, {league_uid})
        },
        async sendPaymentEmails(league_uid: string): Promise<{success: boolean; sent_count: number; skipped_count: number; stripe_not_ready?: boolean; message?: string}> {
            return apiActionImproved('leagues-send-payment-emails', {league_uid})
        },
        async notifyScheduleWhatsApp(league_uid: string): Promise<{enabled: boolean; sent: number; recipients: number}> {
            return apiActionImproved('leagues-notify-schedule' as any, {uid: league_uid})
        },
        async emailSchedule(league_uid: string): Promise<{sent: number; recipients: number; no_email: number}> {
            return apiActionImproved('leagues-email-schedule' as any, {uid: league_uid})
        },
    },
    wallet: {
        async stripeDashboardUrl(): Promise<{url: string; error?: string}> {
            return apiRetrieveImproved('wallet-stripe-dashboard-url', {}, {method: 'GET', body: undefined})
        },
        /** Under payments v3 the answer also says the account's type, whether Express is offered for the country, and whether the day is the way. */
        async payoutAccountStatus(): Promise<{details_submitted: boolean; payouts_enabled: boolean; charges_enabled?: boolean; ready?: boolean; card_payments?: string | null; transfers?: string | null; currently_due?: string[]; not_active_message?: string | null; account_type?: 'express' | 'standard' | null; express_offered?: boolean; collect_on_the_day?: boolean; country?: string | null}> {
            return apiRetrieveImproved('referral-payout-account-status')
        },
    },
    slugs: {
        async check(entityType: 'tournaments' | 'leagues' | 'ryder_cups' | 'clashes' | 'clubs', slug: string, excludeUid?: string): Promise<{available: boolean; error?: string}> {
            return apiActionImproved('slugs-check', {entity_type: entityType, slug, exclude_uid: excludeUid})
        },
        async suggest(entityType: 'tournaments' | 'leagues' | 'ryder_cups' | 'clashes' | 'clubs', title: string, excludeUid?: string): Promise<{slug: string; error?: string}> {
            return apiActionImproved('slugs-suggest', {entity_type: entityType, title, exclude_uid: excludeUid})
        },
    },
    marketplace: {
        // Match Making takes no payment through ScorePal: create and join book instantly.
        async startCreatorCheckout(data: Record<string, any>): Promise<{status: 'created'; game_uid: string}> {
            return apiActionImproved('marketplace-start-checkout', data)
        },
        async checkoutStatus(sessionId: string): Promise<{status: 'completed' | 'pending'; game_uid?: string}> {
            return apiActionImproved('marketplace-checkout-status', {session_id: sessionId})
        },
        async bookSlot(gameUid: string, phoneNumber?: string): Promise<{status: 'booked'; game_uid: string}> {
            return apiActionImproved('marketplace-join-checkout', {game_uid: gameUid, phone_number: phoneNumber})
        },
        async checkIn(gameUid: string): Promise<{status: 'confirmed'; checked_in_at: string}> {
            return apiActionImproved('marketplace-check-in', {game_uid: gameUid})
        },
        async getInviteShareDetails(inviteToken: string): Promise<any> {
            return apiActionImproved('bookings-invite-share-details', {invite_token: inviteToken})
        },
        async startInviteShareCheckout(inviteToken: string): Promise<{redirect_url: string; session_id: string}> {
            return apiActionImproved('bookings-invite-share-checkout', {invite_token: inviteToken})
        },
        async cancelBooking(bookingUid: string): Promise<{success: boolean}> {
            return apiActionImproved('bookings-cancel-with-refund', {booking_uid: bookingUid})
        },
        async cancelGame(gameUid: string): Promise<{success: boolean}> {
            return apiActionImproved('games-cancel-with-refund', {game_uid: gameUid})
        },
    },
    tournaments:   {
        // Discord on an esports event: one channel webhook per tournament, events picked by the organizer.
        async discordShow(uid: string): Promise<{integration: any | null; events: string[]; has_bot: boolean}> {
            return apiActionImproved('tournament-discord-show' as any, {uid})
        },
        async discordStore(uid: string, webhook_url: string, events: string[]): Promise<{integration: any}> {
            return apiActionImproved('tournament-discord-store' as any, {uid, webhook_url, events})
        },
        async discordEvents(uid: string, events: string[]): Promise<{integration: any}> {
            return apiActionImproved('tournament-discord-events' as any, {uid, events})
        },
        async discordDisable(uid: string): Promise<{integration: any}> {
            return apiActionImproved('tournament-discord-disable' as any, {uid})
        },
        async discordTest(uid: string): Promise<{queued: boolean; outbox_uid: string}> {
            return apiActionImproved('tournament-discord-test' as any, {uid})
        },
        async create(data: Record<string, any>): Promise<any> {
            return apiActionImproved('tournaments-create', data)
        },
        async find(tournament_uid: string): Promise<any> {
            return apiRetrieveImproved('tournaments-find', {uid: tournament_uid})
        },
        /** Move an entry between divisions (null = none): its team and registration move together. */
        async entryDivision(data: {tournament_uid: string; application_uid?: string; team_uid?: string; division_key: string | null}): Promise<any> {
            return apiActionImproved('tournament-entry-division' as any, data)
        },
        /** End-of-event "road to victory". Public — the live view is shared with players and spectators. */
        async roundup(tournament_uid: string): Promise<{roundup: TournamentRoundup | null}> {
            return apiRetrieveImproved('tournaments-roundup' as any, {uid: tournament_uid})
        },
        async update(uid: string, values: any): Promise<any> {
            return apiActionImproved('tournaments-update', {uid, values})
        },
        async generateSchedule(uid: string): Promise<{updated: number; pools: number; skipped: Record<string, string>}> {
            return apiActionImproved('tournaments-generate-schedule', {uid})
        },
        async notifyScheduleWhatsApp(uid: string): Promise<{enabled: boolean; sent: number; recipients: number}> {
            return apiActionImproved('tournaments-notify-schedule' as any, {uid})
        },
        async emailSchedule(uid: string): Promise<{sent: number; recipients: number; no_email: number}> {
            return apiActionImproved('tournaments-email-schedule' as any, {uid})
        },
        async playersWithoutMobile(uid: string): Promise<{total: number; missing: {uid: string; name: string}[]}> {
            return apiActionImproved('tournaments-players-without-mobile' as any, {uid})
        },
        async sendPaymentEmails(tournament_uid: string): Promise<{success: boolean; sent_count: number; skipped_count: number; stripe_not_ready?: boolean; message?: string}> {
            return apiActionImproved('tournaments-send-payment-emails' as any, {tournament_uid})
        },
        async stages(tournament_uid: string, division_key?: string | null): Promise<any> {
            return apiRetrieveImproved('tournament-stages', {tournament_uid, division_key: division_key || undefined})
        },
        async players(tournament_uid: string): Promise<any> {
            return apiRetrieveImproved('players-get', {tournament_uid})
        },
        async teams(tournament_uid: string): Promise<any[]> {
            return apiRetrieveImproved('teams-get' as any, {tournament_uid})
        },
        async invite(tournament_uid: string, invited_player_uid: string): Promise<{application_uid: string}> {
            return apiActionImproved('tournaments-invite' as any, {tournament_uid, invited_player_uid})
        },
        async createTeam(tournament_uid: string, p1_uid: string, p2_uid: string): Promise<any> {
            return apiActionImproved('teams-create', {tournament_uid, p1_uid, p2_uid})
        },
        async unassignedPlayers(tournament_uid: string): Promise<any[]> {
            return apiRetrieveImproved('tournaments-players-unassigned' as any, {tournament_uid})
        },
        async applications(tournament_uid: string): Promise<any[]> {
            return apiRetrieveImproved('applications-get' as any, {tournament_uid})
        },
        async approveApplication(uid: string, version: number): Promise<any> {
            return apiActionImproved('applications-update', {uid, values: {status: 2, version}})
        },
        createDraw: function (params: App.Data.CreateDrawRequestData) {
            return apiAction('tournament-draw-create', params);
        },
        /** Same request as createDraw, generated inside a transaction and rolled back: a look, not a draw. */
        async previewDraw(params: App.Data.CreateDrawRequestData): Promise<{preview: true; stages: any[]}> {
            return apiActionImproved('tournament-draw-preview' as any, params as any);
        },
        /** Manual fixtures CSV → matched fixtures + row errors. Writes nothing. Multipart: uid, file, tz_offset_minutes. */
        async parseFixturesCsv(formData: FormData): Promise<{fixtures: any[]; errors: {row: number; message: string}[]; columns: Record<string, boolean>}> {
            return apiActionImproved('tournament-fixtures-csv-parse' as any, formData, {body: formData})
        },
        nextSwissRound: function (draws_stage_uid: string) {
            return apiActionImproved('tournament-draw-next-round' as any, {draws_stage_uid});
        },
        /** Player-reported results (esports events only). All reject on error — never swallow. */
        async myMatches(uid: string): Promise<{enabled: boolean; confirm_minutes: number; matches: any[]}> {
            return apiActionImproved('tournament-my-matches' as any, {uid})
        },
        async reportResult(game_uid: string, sets: [number, number][]): Promise<{success: boolean; game: any}> {
            return apiActionImproved('tournament-result-report' as any, {game_uid, sets})
        },
        async confirmResult(game_uid: string, confirm: boolean, note?: string): Promise<{success: boolean; game: any}> {
            return apiActionImproved('tournament-result-confirm' as any, {game_uid, confirm, note: note || null})
        },
        async resolveResult(tournament_uid: string, game_uid: string, sets: [number, number][], note?: string): Promise<{success: boolean; game: any}> {
            return apiActionImproved('tournament/match-centre/resolve' as any, {tournament_uid, game_uid, sets, note: note || null})
        },
        /** Starter override on a disputed match: score only, no note. */
        async setResult(tournament_uid: string, game_uid: string, sets: [number, number][]): Promise<{success: boolean; game: any}> {
            return apiActionImproved('tournament/match-centre/set-result' as any, {tournament_uid, game_uid, sets})
        },
        /** Explicit reset of later matches before an upstream result changes (the 409 lists them). */
        async resetMatches(tournament_uid: string, game_uids: string[]): Promise<{success: boolean; reset: string[]}> {
            return apiActionImproved('tournament/match-centre/reset-matches' as any, {tournament_uid, game_uids})
        },
        async matchTimeline(tournament_uid: string, game_uid: string): Promise<{timeline: any[]; evidence: any[]}> {
            return apiActionImproved('tournament/match-centre/match-timeline' as any, {tournament_uid, game_uid})
        },
        /** Multipart: game_uid, note, images[] (max 3). Rejects on error like the rest. */
        async attachEvidence(formData: FormData): Promise<{success: boolean; evidence: {note: string | null; images: any[]; kind: string}}> {
            return apiActionImproved('tournament-result-evidence' as any, formData, {body: formData})
        },
    },
    rankingGroups: {
        async get(sport_type?: string): Promise<any[]> {
            return apiRetrieveImproved('ranking-groups-get', {sport_type})
        },
        async create(data: { name: string; sport_type?: string }): Promise<any> {
            return apiActionImproved('ranking-groups-create', data)
        },
    },
    booking:       {
        getAvailableCourts: function (data: { club_uid: string, start_date: any, include_private?: boolean }): Promise<CourtAvailabilityResponse> {
            return apiRetrieveImproved('booking-available-courts', data)
        },
        /** The slot picker: bookable starts per public court for a date and length, priced by the server. */
        startTimes(data: { club_uid: string; start_date: string; duration_minutes: number; sport?: string | null }): Promise<StartTimesResponse> {
            return apiRetrieveImproved('booking-start-times', data)
        },
        getBookings:        function (data: { upcoming: boolean }): Promise<{ bookings: Booking[] }> {
            return apiRetrieveImproved('booking-get', data)
        },
        getClubBookings:    function (data: object): Promise<{ bookings: Booking[] }> {
            return apiRetrieveImproved('booking-get', data)
        },
        // The price is never sent: the server computes it from the club's court pricing.
        book(params: BookingStartParams): Promise<BookingStartResponse> {
            return apiAction('booking-book', params);
        },
        quote(params: { court_uid: string; start_date: any; start_time: string | null; duration_minutes: number; max_players?: number }): Promise<BookingQuote> {
            return apiAction('booking-quote', params);
        },
        block(params: { court_uid: string; duration_minutes: any; start_date: any; start_time: string | null; message: string; }) {
            return apiAction('booking-block', params);
        },
        async updateBooking(params: Partial<Booking>) {
            return apiAction('booking-update', params);
        },
        async deleteBooking(params: { uid: string }) {
            return apiAction('booking-delete', params);
        },
        /** Cancel and refund (the server decides the window and the amounts). Idempotent. */
        async cancel(params: { uid: string }): Promise<{success?: boolean; status?: number; refund_status?: string | null; refunded_cents?: number | null; currency_code?: string | null; error?: string}> {
            return apiAction('booking-cancel', params);
        },
        async cancelPreview(params: { uid: string }): Promise<CancelPreview> {
            return apiAction('booking-cancel-preview', params);
        },
        /** The booking as an .ics file: {filename, content}. */
        async ics(params: { uid: string }): Promise<{filename: string; content: string; error?: string}> {
            return apiAction('booking-ics', params);
        },
        /** The "Book a court" list: bookable clubs with their next free starts on a date. */
        clubs(data: { start_date: string; duration_minutes?: number; sport?: string | null; text?: string | null }): Promise<BookingClubsResponse> {
            return apiRetrieveImproved('booking-clubs', data)
        },
        getMonthlyAvailability(params: { club_uid: string; year: number; month: number; include_private?: boolean }): Promise<MonthlyAvailabilityResponse> {
            return apiRetrieveImproved('booking-monthly-availability', params);
        },
        /** The organiser's calendar: the day (courts × slots, KPIs, old requests) or the week. */
        calendar(params: { date: string; view?: 'day' | 'week'; club_uid?: string }): Promise<CalendarDay | CalendarWeek> {
            return apiRetrieveImproved('booking-calendar', params);
        },
        /** Block time on one court or every court, optionally repeated weekly; 422 names every clash. */
        blockTime(params: { court_uid: string | null; start_date: string; start_time: string; end_time: string; reason?: string; repeat_weeks?: number; club_uid?: string }): Promise<{success?: boolean; booking_uids?: string[]; count?: number; error?: string}> {
            return apiAction('booking-block', params);
        },
        settings(params: { club_uid?: string } = {}): Promise<BookingSettings> {
            return apiRetrieveImproved('booking-settings-get', params);
        },
        saveSettings(params: Record<string, any>): Promise<BookingSettings & {error?: string}> {
            return apiAction('booking-settings-save', params);
        },
        pricePreview(params: { booking_lengths?: number[]; club_uid?: string } = {}): Promise<PricePreview> {
            return apiRetrieveImproved('booking-price-preview', params);
        },
    },
    clubs:         {
        courts: function (data: { club_uid: string }): Promise<Court[]> {
            return apiRetrieveImproved('courts-get', data)
        },
        // Public Club landing page (scorepal.co/{slug}). Accepts a slug or a uid.
        page: function (slugOrUid: string): Promise<any> {
            return apiRetrieveImproved('club-page' as any, {club_slug: slugOrUid})
        },
        // The public page's Club Overview: the organizer dashboard's blocks over 30d/90d/6m/12m.
        pageOverview: function (slugOrUid: string, range: string): Promise<any> {
            return apiRetrieveImproved('club-page-overview' as any, {club_slug: slugOrUid, range})
        },
    },
    assessments:   {
        async index(): Promise<Assessment[]> {
            return apiRetrieveImproved('assessments' as any, {}, {method: 'GET', body: undefined})
        },
        async show(assessment_uid: string): Promise<{ assessment: Assessment, abilities: string[] }> {
            return apiRetrieveImproved(`assessments/${assessment_uid}` as any, {}, {method: 'GET', body: undefined})
        },
        async store(data: Partial<Assessment>): Promise<Assessment> {
            return apiAction('assessments' as any, data, {method: "POST"})
        },
        async update(data: any): Promise<Assessment[]> {
            return apiAction(`assessments/${data.uid}` as any, data, {method: "PATCH"})
        },
    },
    coaches:       {
        async index(): Promise<Player[]> {
            return apiRetrieveImproved('coaches' as any, {}, {method: 'GET', body: undefined})
        },
        async show(coach_uid: string): Promise<{ assessment: Player, abilities: string[] }> {
            return apiRetrieveImproved(`coaches/${coach_uid}` as any, {}, {method: 'GET', body: undefined})
        },
        async store(data: Partial<Player>): Promise<Player> {
            return apiAction('coaches' as any, data, {method: "POST"})
        },
        async update(data: Partial<Player>): Promise<any> {
            return apiAction(`coaches/${data.uid}` as any, {values: data}, {method: "PATCH"})
        },
        async destroy(data: Partial<Player>): Promise<Player[]> {
            return apiAction(`coaches/${data.uid}` as any, data, {method: "DELETE"})
        },
    },
    conversations: {
        async index(): Promise<Conversation[]> {
            return apiRetrieveImproved('conversations' as any, {}, {method: 'GET', body: undefined})
        },
        async show(conversation_uid: string): Promise<{ conversation: Conversation, messages: Message[] }> {
            return apiRetrieveImproved(`conversations/${conversation_uid}` as any, {}, {method: 'GET', body: undefined})
        },
        async store(recipient_uid: string): Promise<Conversation> {
            return apiActionImproved('conversations' as any, {recipient_uid}, {method: "POST"})
        },
        async storeToClub(club_uid: string): Promise<Conversation> {
            return apiActionImproved('conversations' as any, {club_uid}, {method: "POST"})
        },
    },
    messages:      {
        async index(conversation_uid: string): Promise<Message[]> {
            return apiRetrieveImproved(`conversations/${conversation_uid}/messages` as any, {}, {method: 'GET', body: undefined})
        },
        async store(message: Partial<Message>): Promise<Message> {
            return apiActionImproved(`conversations/${message.conversation_uid}/messages` as any, message, {method: "POST"})
        },
    },
    coins:         {
        async stats(): Promise<CoinsStatsResponse> {
            return apiRetrieveImproved(`coins-stats` as any, {}, {method: 'GET', body: undefined})
        },
        async redeem(data: { amazon_region?: string, reward: string, club_custom_reward_uid?: string }) {
            return apiRetrieveImproved(`coins-redeem` as any, data, {method: 'POST'})
        },
        async redemptions() {
            return apiRetrieveImproved(`coins-redemptions` as any, {}, {method: 'GET', body: undefined})
        },
        async updateRedemption(params: { uid: string, version: number, fulfilled?: boolean }) {
            return apiActionImproved('coins-redemption-update' as any, params, {method: 'POST'});
        },
    },
    clubCustomRewards: {
        async list(club_uid: string): Promise<ClubCustomReward[]> {
            return apiRetrieveImproved('club-custom-rewards-get' as any, {club_uid})
        },
        async create(data: { club_uid: string; title: string; coins_cost: number; per_player_limit?: number | null; enabled?: boolean }): Promise<ClubCustomReward> {
            return apiActionImproved('club-custom-rewards-create', data)
        },
        async update(uid: string, values: Partial<Pick<ClubCustomReward, 'title' | 'coins_cost' | 'per_player_limit' | 'enabled'>>): Promise<ClubCustomReward> {
            return apiActionImproved('club-custom-rewards-update', {uid, values})
        },
        async delete(uid: string): Promise<{ success: boolean }> {
            return apiActionImproved('club-custom-rewards-delete', {uid})
        },
    },
    americanos:    {
        async create(data: {
            title: string;
            player_uids: string[];
            scoring_type: 'points' | 'time';
            points_per_round?: number;
            time_per_round_minutes?: number;
        }): Promise<{ success: boolean; uid: string; americano: Americano; schedule: any }> {
            return apiActionImproved('americanos-create' as any, data, {method: "POST"})
        },
        async getLeaderboard(uid: string): Promise<AmericanoLeaderboardEntry[]> {
            return apiRetrieveImproved(`americanos/${uid}/leaderboard` as any, {}, {method: 'GET', body: undefined})
        },
        async submitScore(uid: string, round: number, court: number, data: { team_a_score: number; team_b_score: number }): Promise<{ success: boolean; game: AmericanoGame }> {
            return apiActionImproved(`americanos/${uid}/round/${round}/court/${court}/submit-score` as any, data, {method: "POST"})
        },
        async getCurrentRound(uid: string): Promise<{ current_round: number; total_rounds: number }> {
            return apiRetrieveImproved(`americanos/${uid}/current-round` as any, {}, {method: 'GET', body: undefined})
        },
        async getRoundGames(uid: string, round: number): Promise<AmericanoGame[]> {
            return apiRetrieveImproved(`americanos/${uid}/round/${round}/games` as any, {}, {method: 'GET', body: undefined})
        },
        async getPlayerStats(uid: string, playerUid: string): Promise<AmericanoPlayerStats> {
            return apiRetrieveImproved(`americanos/${uid}/player-stats/${playerUid}` as any, {}, {method: 'GET', body: undefined})
        },
    },
    mexicanos:     {
        async create(data: {
            title: string;
            player_uids: string[];
            scoring_type: 'points' | 'time';
            points_per_round?: number;
            time_per_round_minutes?: number;
            round_count?: number;
        }): Promise<{ success: boolean; uid: string; mexicano: Mexicano; schedule: any }> {
            return apiActionImproved('mexicanos-create' as any, data, {method: "POST"})
        },
        async getLeaderboard(uid: string): Promise<MexicanoLeaderboardEntry[]> {
            return apiRetrieveImproved(`mexicanos/${uid}/leaderboard` as any, {}, {method: 'GET', body: undefined})
        },
        async submitScore(uid: string, round: number, court: number, data: { team_a_score: number; team_b_score: number }): Promise<{ success: boolean; game: MexicanoGame }> {
            return apiActionImproved(`mexicanos/${uid}/round/${round}/court/${court}/submit-score` as any, data, {method: "POST"})
        },
        async getCurrentRound(uid: string): Promise<{ current_round: number; total_rounds: number }> {
            return apiRetrieveImproved(`mexicanos/${uid}/current-round` as any, {}, {method: 'GET', body: undefined})
        },
        async getRoundGames(uid: string, round: number): Promise<MexicanoGame[]> {
            return apiRetrieveImproved(`mexicanos/${uid}/round/${round}/games` as any, {}, {method: 'GET', body: undefined})
        },
        async getPlayerStats(uid: string, playerUid: string): Promise<MexicanoPlayerStats> {
            return apiRetrieveImproved(`mexicanos/${uid}/player-stats/${playerUid}` as any, {}, {method: 'GET', body: undefined})
        },
    },
    kotc:          {
        async create(data: {
            title: string;
            player_uids: string[];
            round_count: number;
            scoring_type: 'points' | 'time';
            points_per_round?: number;
            time_per_round_minutes?: number;
            standings_mode?: 'points' | 'court';
        }): Promise<{ success: boolean; uid: string; king_of_the_court: League; round: any }> {
            return apiActionImproved('kotc-create' as any, data, {method: "POST"})
        },
        async getLeaderboard(uid: string): Promise<KotcLeaderboard> {
            return apiRetrieveImproved(`kotc/${uid}/leaderboard` as any, {}, {method: 'GET', body: undefined})
        },
        async submitScore(uid: string, round: number, court: number, data: { team_a_score: number; team_b_score: number }): Promise<{ success: boolean; game: AmericanoGame }> {
            return apiActionImproved(`kotc/${uid}/round/${round}/court/${court}/submit-score` as any, data, {method: "POST"})
        },
        async getCurrentRound(uid: string): Promise<{ current_round: number; total_rounds: number }> {
            return apiRetrieveImproved(`kotc/${uid}/current-round` as any, {}, {method: 'GET', body: undefined})
        },
        async getRoundGames(uid: string, round: number): Promise<AmericanoGame[]> {
            return apiRetrieveImproved(`kotc/${uid}/round/${round}/games` as any, {}, {method: 'GET', body: undefined})
        },
    },
    reviews:       {
        async index(club_uid: string): Promise<Review[]> {
            return apiRetrieveImproved(`reviews?${new URLSearchParams({club_uid}).toString()}` as any, {}, {method: 'GET', body: undefined})
        },
        async store(data: Partial<Review>): Promise<Review> {
            return apiActionImproved('reviews' as any, data, {method: "POST"})
        },
        async destroy(data: Partial<Review>): Promise<Review[]> {
            return apiActionImproved(`reviews/${data.uid}` as any, data, {method: "DELETE"})
        },
    },
    races:         {
        async list(): Promise<any[]> {
            return apiRetrieveImproved('races' as any, {}, {method: 'GET', body: undefined})
        },
        async dashboard(): Promise<any> {
            return apiRetrieveImproved('races-dashboard' as any, {}, {method: 'GET', body: undefined})
        },
        async create(data: any): Promise<any> {
            return apiActionImproved('races' as any, data, {method: 'POST'})
        },
        async update(uid: string, data: any): Promise<any> {
            return apiActionImproved(`races/${uid}/update` as any, data, {method: 'POST'})
        },
        async remove(uid: string): Promise<any> {
            return apiActionImproved(`races/${uid}/delete` as any, {}, {method: 'POST'})
        },
        // Race-day list: everyone entered, plus the checked-in/finished tallies.
        async entries(uid: string): Promise<any> {
            return apiRetrieveImproved(`races/${uid}/entries` as any, {}, {method: 'GET', body: undefined})
        },
        async addEntry(uid: string, data: any): Promise<any> {
            return apiActionImproved(`races/${uid}/entries` as any, data, {method: 'POST'})
        },
        async updateEntry(uid: string, entryUid: string, data: any): Promise<any> {
            return apiActionImproved(`races/${uid}/entries/${entryUid}` as any, data, {method: 'POST'})
        },
        // Public — no login needed. `handle` is the race slug or uid.
        async publicRace(handle: string): Promise<any> {
            return apiRetrieveImproved(`public-race/${handle}` as any, {}, {method: 'GET', body: undefined})
        },
        async register(handle: string, data: any): Promise<any> {
            return apiActionImproved(`public-race/${handle}/register` as any, data, {method: 'POST'})
        },
        async selfCheckIn(handle: string, data: any): Promise<any> {
            return apiActionImproved(`public-race/${handle}/check-in` as any, data, {method: 'POST'})
        },
    },
    running:       {
        async getImports(): Promise<any> {
            return apiRetrieveImproved(`running-imports` as any, {}, {method: 'GET', body: undefined})
        },
        async getResults(uid: string): Promise<RunningGetResultsResponse> {
            return apiRetrieveImproved(`running-results` as any, {uid}, {method: 'POST'})
        },
        async getLeaderboardResults(ranking_group_uid: string, data: { sort_by: string, category: string, gender: string }): Promise<any> {
            return apiRetrieveImproved(`running-leaderboard-results` as any, {...data, ranking_group_uid}, {method: 'POST'})
        },
        async getRankings(): Promise<any> {
            return apiRetrieveImproved(`running-rankings` as any, {}, {method: 'GET', body: undefined})
        },
        async deleteImport(uid: string): Promise<any> {
            return apiActionImproved(`delete-import` as any, {uid}, {method: 'POST'})
        },
        async updateImport(values: any): Promise<any> {
            return apiActionImproved(`update-import` as any, values, {method: 'POST'})
        },
    },
    clubSubscription: {
        /**
         * `opts.ack_trial_start` marks the one-time "your trial started" message as shown. It rides
         * on this endpoint because the acknowledgement and the state it acknowledges are the same
         * resource — the response comes back already reflecting it, so the two cannot disagree.
         */
        async info(club_uid: string, opts?: {ack_trial_start?: boolean}): Promise<{
            subscription_tier: ClubSubscriptionTierType;
            subscription_tier_label: string;
            subscription_status: number;
            subscription_expires_at: string | null;
            is_active: boolean;
            usage: {
                entries_per_league: { current: number; limit: number };
                active_leagues: { current: number; limit: number };
                active_tournaments: { current: number; limit: number };
            };
            features: Record<string, boolean>;
            requires_branding: boolean;
        }> {
            return apiActionImproved('club-subscription/info' as any, {club_uid, ...(opts ?? {})}, {method: 'POST'})
        },
        async checkout(data: { club_uid: string; plan: number; currency_code: string; onboarding?: boolean }): Promise<{ redirect_url: string }> {
            return apiActionImproved('club-subscription/checkout' as any, data, {method: 'POST'})
        },
        async billingPortal(club_uid: string): Promise<{ portal_url: string }> {
            return apiActionImproved('club-subscription/billing-portal' as any, {club_uid}, {method: 'POST'})
        },
    },
    /** The $2.99 Plus trial (plus_trial_enabled). */
    plusTrial:     {
        async status(club_uid: string): Promise<PlusTrialPicture> {
            return apiActionImproved('plus-trial/status' as any, {club_uid}, {method: 'POST'})
        },
        async start(club_uid: string, currency_code = 'USD'): Promise<{redirect_url: string}> {
            return apiActionImproved('plus-trial/start' as any, {club_uid, currency_code}, {method: 'POST'})
        },
        async cancel(club_uid: string): Promise<{cancelled: boolean; ends_at: string | null}> {
            return apiActionImproved('plus-trial/cancel' as any, {club_uid}, {method: 'POST'})
        },
    },
    pricing:       {
        async getPlans(): Promise<Record<number, { price: number; price_regular: number; discount_percent: number, currency_code: string }>> {
            return apiRetrieveImproved('pricing-plans' as any, {}, {method: 'POST'})
        },
        async getFees(): Promise<PricingFeesResponse> {
            return apiRetrieveImproved('pricing-fees' as any, {}, {method: 'GET', body: undefined})
        },
    },
    ryderCup:      {
        async index(): Promise<any[]> {
            return apiRetrieveImproved('ryder-cups' as any, {}, {method: 'GET', body: undefined})
        },
        async show(uid: string): Promise<any> {
            return apiRetrieveImproved(`ryder-cups/${uid}` as any, {}, {method: 'GET', body: undefined})
        },
        async store(data: any): Promise<any> {
            return apiActionImproved('ryder-cups' as any, data, {method: 'POST'})
        },
        async update(uid: string, values: any): Promise<any> {
            return apiActionImproved(`ryder-cups/${uid}` as any, {values}, {method: 'PATCH'})
        },
        async destroy(uid: string): Promise<any> {
            return apiActionImproved(`ryder-cups/${uid}` as any, {}, {method: 'DELETE'})
        },
        async getRoster(uid: string): Promise<any[]> {
            return apiRetrieveImproved(`ryder-cups/${uid}/roster` as any, {}, {method: 'GET', body: undefined})
        },
        async addToRoster(uid: string, data: { player_uid: string; side: 'a' | 'b' }): Promise<any> {
            return apiActionImproved(`ryder-cups/${uid}/roster` as any, data, {method: 'POST'})
        },
        async removeFromRoster(uid: string, playerUid: string): Promise<any> {
            return apiActionImproved(`ryder-cups/${uid}/roster/${playerUid}` as any, {}, {method: 'DELETE'})
        },
        async getRounds(uid: string): Promise<any[]> {
            return apiRetrieveImproved(`ryder-cups/${uid}/rounds` as any, {}, {method: 'GET', body: undefined})
        },
        async dashboard(uid: string): Promise<any> {
            return apiRetrieveImproved(`ryder-cups/${uid}/dashboard` as any, {}, {method: 'GET', body: undefined})
        },
        async getMatches(uid: string): Promise<any[]> {
            return apiRetrieveImproved(`ryder-cups/${uid}/matches` as any, {}, {method: 'GET', body: undefined})
        },
        async createMatch(uid: string, data: any): Promise<any> {
            return apiActionImproved(`ryder-cups/${uid}/matches` as any, data, {method: 'POST'})
        },
        async updateMatch(uid: string, matchUid: string, values: any): Promise<any> {
            return apiActionImproved(`ryder-cups/${uid}/matches/${matchUid}` as any, {values}, {method: 'PATCH'})
        },
        async deleteMatch(uid: string, matchUid: string): Promise<any> {
            return apiActionImproved(`ryder-cups/${uid}/matches/${matchUid}` as any, {}, {method: 'DELETE'})
        },
        async randomDraw(uid: string, round: number): Promise<any[]> {
            return apiActionImproved(`ryder-cups/${uid}/random-draw/${round}` as any, {}, {method: 'POST'})
        },
        async reset(uid: string): Promise<any> {
            return apiActionImproved(`ryder-cups/${uid}/reset` as any, {}, {method: 'POST'})
        },
    },
    publicEvent: {
        async show(uid: string): Promise<any> {
            return apiRetrieveImproved('event-public', {uid})
        },
        async playerSchedule(token: string): Promise<any> {
            return apiRetrieveImproved('player-schedule', {token})
        },
        async list(filters: {sport?: string; q?: string; sort?: string} = {}): Promise<{events: any[]; recent: any[]}> {
            return apiRetrieveImproved('event-public-list', filters)
        },
        async apply(data: {tournament_uid?: string; league_uid?: string; race_uid?: string; partner_uid?: string; division?: string; form_answers?: Record<string, string>; waivers_accepted?: string[]}): Promise<{application_uid: string; status?: number; waitlisted?: boolean; approved?: boolean; requires_approval?: boolean; pay_on_the_day?: boolean}> {
            return apiActionImproved('event-apply', data)
        },
        async registerGuest(data: {tournament_uid?: string; league_uid?: string; team_name: string; name: string; email: string; division?: string; form_answers?: Record<string, string>; waivers_accepted?: string[]}): Promise<{application_uid: string; player_uid: string; payment_required: boolean; entry_amount?: number | null; entry_currency_code?: string | null; approved?: boolean; requires_approval?: boolean; pay_on_the_day?: boolean}> {
            return apiActionImproved('event-register-guest', data)
        },
        async joinPartnerSearch(data: {tournament_uid?: string; league_uid?: string; plays?: string; side?: string; experience?: string}): Promise<{uid: string}> {
            return apiActionImproved('partner-seeker-join', data)
        },
        async payPartnerEntry(application_uid: string): Promise<{redirect_url?: string; error?: string}> {
            return apiActionImproved('stripe-prepare-partner-entry-payment' as any, {application_uid})
        },
        async leavePartnerSearch(data: {tournament_uid?: string; league_uid?: string}): Promise<{success: boolean}> {
            return apiActionImproved('partner-seeker-leave', data)
        },
        async applyDivisions(data: {tournament_uid?: string; league_uid?: string; race_uid?: string; divisions: {division_key: string; team_name?: string; squad?: string[]; partner_uid?: string; partner_name?: string; partner_email?: string}[]; form_answers?: Record<string, string>; waivers_accepted?: string[]; players?: {uid: string; email?: string; phone?: string}[]}): Promise<{
            applications: {application_uid: string; division_key: string; division_name: string; entry_amount: number; approved?: boolean}[];
            requires_approval?: boolean;
            total_cents: number;
            currency: string | null;
            waitlisted: boolean;
        }> {
            return apiActionImproved('event-apply-divisions' as any, data)
        },
    },
    /** /build/ (BuildQuiz, behind build_quiz_enabled): the draw before sign-up. Public but the claim. */
    // Share the results of a finished event (share_results_enabled). Public endpoints.
    results: {
        /** The share payload; `view` counts a results-page view (one row per event per day). */
        async get(uid: string, view = false): Promise<import('../results/resultsModel').ResultsPayload> {
            return apiActionImproved('event-results' as any, {uid, view})
        },
        async shared(uid: string, channel: 'whatsapp' | 'copy' | 'image' | 'story'): Promise<{recorded: boolean}> {
            return apiActionImproved('event-results-shared' as any, {uid, channel})
        },
    },
    build: {
        async config(): Promise<BuildConfig> {
            return apiRetrieveImproved('build/config' as any, {}, {method: 'GET', body: undefined} as any)
        },
        async start(data: Record<string, unknown>): Promise<BuildPicture & {token: string}> {
            return apiActionImproved('build/start' as any, data)
        },
        async state(token: string): Promise<BuildPicture> {
            return apiActionImproved('build/state' as any, {token})
        },
        async draw(token: string, format: string): Promise<BuildPicture> {
            return apiActionImproved('build/draw' as any, {token, format})
        },
        async claim(token: string): Promise<{claimed: boolean; event_uid: string; kind: string; route: string | null; format: string | null; sport_type: string; club_uid: string | null}> {
            return apiActionImproved('build/claim' as any, {token})
        },
        /** A funnel beacon (quiz_started, question_answered, link_shared). Fire-and-forget: never awaited, never fails a step. */
        funnel(data: {event: 'quiz_started' | 'question_answered' | 'link_shared'; funnel: 'build' | 'quiz'; key?: string; step?: number; channel?: string; src?: string; sport?: string; token?: string}): void {
            apiActionImproved('funnel-events' as any, data).catch(() => {});
        },
    },
    /** Event quiz v2 (/event-quiz/), behind event_quiz_enabled. Public until the signup logs the organizer in. */
    eventQuiz: {
        /** The home page's "describe your event" text, read into the quiz's keys. */
        async intake(text: string): Promise<{intake: import('../onboarding/intake.ts').Intake}> {
            return apiActionImproved('event-quiz/intake' as any, {text})
        },
        async submit(data: Record<string, unknown>): Promise<{uid: string}> {
            return apiActionImproved('event-quiz/submissions' as any, data)
        },
        async signup(uid: string, data: Record<string, unknown>): Promise<EventQuizSignupResponse> {
            return apiActionImproved(`event-quiz/submissions/${uid}/signup` as any, data)
        },
    },
    siteAssistant: {
        async ask(message: string, sessionId?: string, context?: 'organizer_profile'): Promise<{answer: string; session_id: string; error?: boolean}> {
            return apiActionImproved('site-assistant-ask' as any, {message, session_id: sessionId, ...(context ? {context} : {})})
        },
    },
    clubDirectory: {
        async list(filters: {sport?: string; q?: string; city?: string} = {}): Promise<{clubs: any[]}> {
            return apiRetrieveImproved('club-listings-public' as any, filters)
        },
        async create(data: Record<string, any>): Promise<{uid?: string; status?: string; error?: string}> {
            return apiActionImproved('club-listings-create' as any, data)
        },
    },
    clashOfTeams: {
        async index(): Promise<any[]> {
            return apiRetrieveImproved('clash-of-teams' as any, {}, {method: 'GET', body: undefined})
        },
        async show(uid: string): Promise<any> {
            return apiRetrieveImproved(`clash-of-teams/${uid}` as any, {}, {method: 'GET', body: undefined})
        },
        async create(data: any): Promise<any> {
            return apiActionImproved('clash-of-teams' as any, data, {method: 'POST'})
        },
        async update(uid: string, values: any): Promise<any> {
            return apiActionImproved(`clash-of-teams/${uid}` as any, {values}, {method: 'PATCH'})
        },
        async destroy(uid: string): Promise<any> {
            return apiActionImproved(`clash-of-teams/${uid}` as any, {}, {method: 'DELETE'})
        },
        async dashboard(uid: string): Promise<any> {
            return apiRetrieveImproved(`clash-of-teams/${uid}/dashboard` as any, {}, {method: 'GET', body: undefined})
        },
        async updateTeam(uid: string, teamUid: string, values: any): Promise<any> {
            return apiActionImproved(`clash-of-teams/${uid}/teams/${teamUid}` as any, {values}, {method: 'PATCH'})
        },
        async addRosterMember(uid: string, data: {team_uid: string; player_uid: string}): Promise<any> {
            return apiActionImproved(`clash-of-teams/${uid}/roster-members` as any, data, {method: 'POST'})
        },
        async removeRosterMember(uid: string, rosterUid: string): Promise<any> {
            return apiActionImproved(`clash-of-teams/${uid}/roster-members/${rosterUid}` as any, {}, {method: 'DELETE'})
        },
        async createMatch(uid: string, data: {
            week_uid: string; team_a_uid: string; team_b_uid: string;
            team_a_player_1_uid: string; team_a_player_2_uid?: string;
            team_b_player_1_uid: string; team_b_player_2_uid?: string;
            round?: number | null; court?: string | null;
        }): Promise<any> {
            return apiActionImproved(`clash-of-teams/${uid}/matches` as any, data, {method: 'POST'})
        },
        /** A fixture list from a CSV. preview: true only reports; false imports all of it or none. */
        async importFixtures(uid: string, data: {csv: string; week_uid?: string | null; preview: boolean}): Promise<any> {
            return apiActionImproved(`clash-of-teams/${uid}/fixtures-import` as any, data, {method: 'POST'})
        },
        async updateMatch(uid: string, matchUid: string, values: any): Promise<any> {
            return apiActionImproved(`clash-of-teams/${uid}/matches/${matchUid}` as any, {values}, {method: 'PATCH'})
        },
        async deleteMatch(uid: string, matchUid: string): Promise<any> {
            return apiActionImproved(`clash-of-teams/${uid}/matches/${matchUid}` as any, {}, {method: 'DELETE'})
        },
    },
    clubGroups:    {
        async index(club_uid: string): Promise<ClubGroup[]> {
            return apiRetrieveImproved(`club-groups?${new URLSearchParams({club_uid}).toString()}` as any, {}, {method: 'GET', body: undefined})
        },
        async show(uid: string): Promise<ClubGroup> {
            return apiRetrieveImproved(`club-groups/${uid}` as any, {}, {method: 'GET', body: undefined})
        },
        async store(data: { club_uid: string; name: string; player_uids?: string[] }): Promise<ClubGroup> {
            return apiActionImproved('club-groups' as any, data, {method: 'POST'})
        },
        async update(uid: string, data: { values: { version: number; name?: string }; player_uids?: string[] }): Promise<ClubGroup> {
            return apiActionImproved(`club-groups/${uid}` as any, data, {method: 'PATCH'})
        },
        async destroy(uid: string): Promise<{ success: boolean }> {
            return apiActionImproved(`club-groups/${uid}` as any, {}, {method: 'DELETE'})
        },
        async addMember(group_uid: string, data: { player_uid: string; manual_grade?: string }): Promise<ClubGroupMember> {
            return apiActionImproved(`club-groups/${group_uid}/members` as any, data, {method: 'POST'})
        },
        async removeMember(group_uid: string, member_uid: string): Promise<{ success: boolean }> {
            return apiActionImproved(`club-groups/${group_uid}/members/${member_uid}` as any, {}, {method: 'DELETE'})
        },
        async updateMember(group_uid: string, member_uid: string, data: { values: { version: number; manual_grade?: string } }): Promise<ClubGroupMember> {
            return apiActionImproved(`club-groups/${group_uid}/members/${member_uid}` as any, data, {method: 'PATCH'})
        },
        async message(uid: string, data: { message: string }): Promise<{ success: boolean; sent_count: number }> {
            return apiActionImproved(`club-groups/${uid}/message` as any, data, {method: 'POST'})
        },
    },
    cupClashPayment: {
        // Web: opens Stripe Checkout. Master role: bypass + entity created inline.
        async prepare(data: { entity_type: 'ryder_cup' | 'clash_of_teams' | 'americano' | 'mexicano' | 'king_of_the_court'; payload: any; currency_code?: string }): Promise<{ redirect_url?: string; transaction_uid?: string; master_bypass?: boolean; entity_type?: string; entity_uid?: string }> {
            return apiActionImproved('stripe-prepare-cup-clash-payment', data, {method: 'POST'})
        },
        // Frontend polls after Stripe redirect-back to read the created entity uid.
        async check(transaction_uid: string): Promise<{ success: boolean; entity_type: string | null; entity_uid: string | null }> {
            return apiActionImproved('stripe-check-cup-clash-payment', {transaction_uid}, {method: 'POST'})
        },
        // $2.99/mo events subscription — the alternative to the per-event fee. Opens Stripe Checkout
        // in subscription mode and returns to `return_path` with ?events_subscribed=1.
        async subscribeEvents(return_path: string): Promise<{ redirect_url?: string; already_active?: boolean; error?: string }> {
            return apiActionImproved('stripe-subscribe-events', {return_path}, {method: 'POST'})
        },
        // Whether one-off events are currently ad-supported (free) or paid.
        async options(): Promise<{ free: boolean }> {
            return apiRetrieveImproved('cup-clash-event-options' as any, {})
        },
        // "Watch a video, create free" — ask for a challenge before showing the rewarded ad…
        async rewardStart(data: { entity_type: string; payload: any }): Promise<{ token?: string; issued_at?: number; remaining?: number; available?: boolean; error?: string }> {
            return apiActionImproved('cup-clash-reward-start' as any, data, {method: 'POST'})
        },
        // …then redeem it once the ad has actually been watched.
        async rewardCreate(data: { entity_type: string; payload: any; token: string; issued_at: number }): Promise<{ ad_reward?: boolean; entity_uid?: string; remaining?: number; error?: string; quota_used?: boolean }> {
            return apiActionImproved('cup-clash-reward-create' as any, data, {method: 'POST'})
        },
    },
    // Session sign-ups by WhatsApp: a club's sessions, names, link and post.
    clubSessions: {
        async list(club_uid: string): Promise<ClubSessionsOverview> {
            return apiRetrieveImproved('club-sessions' as any, {club_uid})
        },
        async create(club_uid: string, values: SessionValues & {type_label?: string | null; heading?: SessionHeading | null}, extra: {repeat_weeks?: number; names?: ListedName[]; waitlist?: string[]; layout?: PostLayout | null} = {}): Promise<ClubSessionsOverview & {not_added: string[]; created: string[]; updated: string[]}> {
            return apiActionImproved('club-sessions-create' as any, {club_uid, values, ...extra})
        },
        // The version goes with it: apiActionImproved refuses any "-update" call without one. The
        // server does not check it (the page always shows the latest), so it is the session's as last loaded.
        async update(uid: string, version: number, values: Partial<SessionValues>): Promise<ClubSessionsOverview> {
            return apiActionImproved('club-sessions-update' as any, {uid, version, values})
        },
        async setStatus(uid: string, action: 'lock' | 'reopen' | 'cancel'): Promise<ClubSessionsOverview> {
            return apiActionImproved('club-sessions-status' as any, {uid, action})
        },
        async addPlayer(uid: string, who: {name?: string; player_uid?: string}): Promise<ClubSessionsOverview> {
            return apiActionImproved('club-sessions-add-player' as any, {uid, ...who})
        },
        // The club's players whose name begins with what is typed, but for those already on the session.
        async players(club_uid: string, q: string, uid?: string): Promise<SessionPlayerHit[]> {
            return apiActionImproved('club-sessions-players' as any, {club_uid, q, uid})
        },
        // On to the waitlist whether or not there is room; and from it into a free place.
        async reserve(uid: string, who: {name?: string; player_uid?: string}): Promise<ClubSessionsOverview> {
            return apiActionImproved('club-sessions-reserve' as any, {uid, ...who})
        },
        async promote(uid: string, player_uid: string): Promise<ClubSessionsOverview> {
            return apiActionImproved('club-sessions-promote' as any, {uid, player_uid})
        },
        async removePlayer(uid: string, player_uid: string): Promise<ClubSessionsOverview> {
            return apiActionImproved('club-sessions-remove-player' as any, {uid, player_uid})
        },
        async link(club_uid: string, renew = false): Promise<ClubSessionsOverview> {
            return apiActionImproved('club-sessions-link' as any, {club_uid, renew})
        },
        // The signed-in user has seen the WhatsApp Sessions welcome, or opened the page (the sidebar's NEW goes).
        async seen(what: 'welcome' | 'sidebar'): Promise<{success: boolean; what: string; seen_at: string | null}> {
            return apiActionImproved('whatsapp-sessions-seen' as any, {what})
        },
        // The title WhatsApp shows for the link in a group; empty goes back to the default.
        // A result: the score of one of the session's games, or a game that was played and never made.
        async result(uid: string, payload: {game_uid?: string; side_one?: string[]; side_two?: string[]; sets: number[][]}): Promise<ClubSessionsOverview> {
            return apiActionImproved('club-sessions-result' as any, {uid, ...payload})
        },
        async linkTitle(club_uid: string, title: string): Promise<ClubSessionsOverview> {
            return apiActionImproved('club-sessions-link-title' as any, {club_uid, title})
        },
        async parse(club_uid: string, text: string, timezone: string): Promise<{sessions: ParsedSession[]; notes: string[]; layout: PostLayout | null}> {
            return apiActionImproved('club-sessions-parse' as any, {club_uid, text, timezone})
        },
        // How the club runs its week: the weekly post, the reminder, the defaults of a new session.
        async settings(club_uid: string, values: Partial<SessionWeekSettings>): Promise<ClubSessionsOverview> {
            return apiActionImproved('club-sessions-settings' as any, {club_uid, values})
        },
        // The pairs for a session's games, as player uids.
        async pairs(uid: string, pairs: string[][]): Promise<ClubSessionsOverview> {
            return apiActionImproved('club-sessions-pairs' as any, {uid, pairs})
        },
        // A team game: pick the teams (the organizer's own lineup, one list of player uids per
        // team, or nothing to let the session's mode decide), and its one final score with who scored.
        async pickTeams(uid: string, teams?: string[][]): Promise<ClubSessionsOverview & {sheet: string; notes: string[]; changed: number}> {
            return apiActionImproved('club-sessions-pick-teams' as any, teams ? {uid, teams} : {uid})
        },
        async teamResult(uid: string, score: [number, number], scorers?: string): Promise<ClubSessionsOverview> {
            return apiActionImproved('club-sessions-team-result' as any, {uid, score, scorers})
        },
        async makeGames(uid: string): Promise<ClubSessionsOverview & {plan: string; notes: string[]}> {
            return apiActionImproved('club-sessions-make-games' as any, {uid})
        },
        // Give back what a player paid: all that is left of it, or so much.
        async refund(payment_uid: string, amount?: number): Promise<ClubSessionsOverview> {
            return apiActionImproved('club-sessions-refund' as any, {payment_uid, amount})
        },
        // Back to ScorePal's own layout for the post, whatever the pasted list looked like.
        async resetLayout(club_uid: string): Promise<ClubSessionsOverview> {
            return apiActionImproved('club-sessions-layout' as any, {club_uid, reset: true})
        },
        // The public page behind /s/<CODE>.
        async publicPage(code: string): Promise<RegistrationPage> {
            return apiRetrieveImproved('registration-link' as any, {code})
        },
    },

    // Registering for a league or a tournament by WhatsApp: the event's code, links and post.
    eventRegistration: {
        async show(scope: {league_uid: string} | {tournament_uid: string}): Promise<EventRegistrationLink> {
            return apiRetrieveImproved('event-registration-link' as any, scope)
        },
        async make(scope: {league_uid: string} | {tournament_uid: string}, renew = false): Promise<EventRegistrationLink> {
            return apiActionImproved('event-registration-link-create' as any, {...scope, renew})
        },
        // WhatsApp registration off: the event's code is retired, and links already posted stop working.
        async retire(scope: {league_uid: string} | {tournament_uid: string}): Promise<EventRegistrationLink> {
            return apiActionImproved('event-registration-link-retire' as any, scope)
        },
        // The title WhatsApp shows for the link in a group; empty goes back to the event's.
        async title(scope: {league_uid: string} | {tournament_uid: string}, title: string): Promise<EventRegistrationLink> {
            return apiActionImproved('event-registration-link-title' as any, {...scope, title})
        },
    },

    // The link a new player is sent in WhatsApp to finish their profile.
    profileFinish: {
        async show(token: string): Promise<{name: string; lastname: string | null; phone: string}> {
            return apiRetrieveImproved('profile-finish-show' as any, {token})
        },
        async save(token: string, values: {name: string; lastname: string; email: string; password: string}): Promise<any> {
            return apiActionImproved('profile-finish' as any, {token, ...values})
        },
    },

    // The emailed link that connects a WhatsApp number to the signed-in account.
    whatsappLink: {
        async show(token: string): Promise<{ok: boolean; message: string | null; phone: string | null}> {
            return apiActionImproved('whatsapp-link/show' as any, {token})
        },
        async confirm(token: string): Promise<{ok: boolean; phone: string; signup: {club: string; code: string; whatsapp_url: string; web_url: string} | null}> {
            return apiActionImproved('whatsapp-link/confirm' as any, {token})
        },
    },
    accountAdmins: {
        async list(scope?: {tournament_uid?: string; league_uid?: string; clash_uid?: string; ryder_cup_uid?: string}): Promise<{admins: {uid: string; email: string; name: string | null; status: string; accepted_at: string | null}[]}> {
            return apiRetrieveImproved('account-admins-list' as any, scope || {})
        },
        async invite(email: string, scope?: {tournament_uid?: string; league_uid?: string; clash_uid?: string; ryder_cup_uid?: string}): Promise<{success: boolean; existing_user: boolean}> {
            return apiActionImproved('account-admins-invite' as any, {email, ...(scope || {})})
        },
        async accept(token: string): Promise<{success?: boolean; owner_name?: string; already?: boolean; error?: string; redirect_to?: string; event_title?: string}> {
            return apiActionImproved('account-admins-accept' as any, {token})
        },
        async revoke(uid: string): Promise<{success: boolean}> {
            return apiActionImproved('account-admins-revoke' as any, {uid})
        },
    },

    /** Club managers — an organizer hands a saved team's club page and squad to that club's manager. */
    clubManagers: {
        async list(scope: {team_uid?: string; club_uid?: string}): Promise<{
            club: {uid: string; name: string; slug?: string} | null;
            managers: {uid: string; email: string; name: string | null; status: string; accepted_at: string | null; expires_at: string | null}[];
        }> {
            return apiRetrieveImproved('club-managers-list' as any, scope)
        },
        async invite(team_uid: string, email: string): Promise<{success: boolean; uid: string; club_uid: string}> {
            return apiActionImproved('club-managers-invite' as any, {team_uid, email})
        },
        /** Public — what this invite is for, so the accept page can explain itself and pre-fill signup. */
        async preview(token: string): Promise<{email: string; club_name?: string; team_name?: string; sport_type?: string | null; competition?: string | null; has_account: boolean}> {
            return apiRetrieveImproved('club-managers-preview' as any, {token})
        },
        async accept(token: string): Promise<{success?: boolean; already?: boolean; error?: string; club?: {uid: string; name: string; slug?: string}}> {
            return apiActionImproved('club-managers-accept' as any, {token})
        },
        async revoke(uid: string): Promise<{success: boolean}> {
            return apiActionImproved('club-managers-revoke' as any, {uid})
        },
    },
    eventPass: {
        /**
         * `effective_price` is what THIS caller will be charged, for the same `promo`/`kind` it
         * will pass to checkout(). Display that, never `promo` — the promo is opt-in per request
         * and advertising it from a surface that does not send it is how the billing page came to
         * show US$4.99 while Stripe charged US$9.99.
         */
        async status(opts?: {promo?: string; kind?: 'standard' | 'completion' | 'sized'; tournament_uid?: string; league_uid?: string}): Promise<{
            available: number;
            used: number;
            price: {amount_cents: number; currency: string};
            effective_price: {amount_cents: number; currency: string};
            promo?: {amount_cents: number; currency: string; code: string} | null;
            /**
             * Set when `tournament_uid` names one of the caller's tournaments on an ENFORCED one-off
             * path: the size-tiered quote for that event (the same numbers checkout charges) and
             * its earn route. Null for every other organizer, whatever they passed.
             */
            sized?: OneOffSizedQuote | null;
        }> {
            return apiActionImproved('event-pass/status' as any, {...(opts ?? {})})
        },
        // `kind: completion` prices the trial-expiry completion pass. The server re-checks the
        // offer is genuinely open for that league — it cannot be bought speculatively.
        // `kind: sized` + `tournament_uid` is the one-off path's pass for that event: priced by its
        // field server-side and applied to it on fulfilment, so it never sits in the wallet.
        // `src` says where the buy was started from (the follow-up email's button: PASS_FOLLOWUP.src) and
        // travels into Stripe metadata for attribution; omitted from the app.
        async checkout(return_path?: string, promo?: string, kind?: 'standard' | 'completion' | 'sized', league_uid?: string, tournament_uid?: string, tier?: number, offer_code?: string, src?: string): Promise<{redirect_url: string}> {
            return apiActionImproved('event-pass/checkout' as any, {return_path, promo, kind, league_uid, tournament_uid, tier, offer_code, src})
        },
        async confirm(session_id: string): Promise<{created: boolean; available: number}> {
            return apiActionImproved('event-pass/confirm' as any, {session_id})
        },
        async earnStatus(event: {tournament_uid?: string; league_uid?: string}): Promise<{
            /**
             * 'one_off' means the numbers below are the ROSTER rule (people with a verified phone,
             * ceil of the configured share, floored) and `cap` is the free band — not the legacy
             * 9-signed-up-entries pledge. Same fields, so the card renders either picture.
             */
            path: 'one_off' | 'community';
            /** On the one-off path `entries` is the COUNTED field; `total_entries` is everyone. */
            entries: number; total_entries?: number; collects_online?: boolean;
            cap: number; earn_mode: boolean; unlocked: boolean;
            required_signed: number; signed_up_entries: number;
            players: {uid: string; name: string; signed_up: boolean; claimed?: boolean; email: string | null; phone: string | null; version: number}[];
            unlinked_entries: number; signed_up_count: number; total_players: number;
            can_claim: boolean;
            join_link: string | null;
            covered_entries?: number | null;
            earn_applies?: boolean;
            earn_eligible?: boolean;
            earn_min_entrants?: number;
            quote?: OneOffSizedQuote | null;
        }> {
            return apiActionImproved('event-pass/earn-status' as any, event)
        },
        /**
         * The event pages' early-warning read, from the gates' own resolver: the offer to show
         * before any block (null when there is nothing to offer), the pass the event holds and
         * the event-scoped features that pass grants. `surface` is logged as an impression.
         */
        async goLiveStatus(event: {tournament_uid?: string; league_uid?: string; surface?: 'banner' | 'post_create'}): Promise<OneOffGoLiveStatus> {
            return apiActionImproved('event-pass/go-live-status' as any, event)
        },
        /** Which route of the Event Pass offer was clicked — read by Master Admin → Activation. Never blocks. */
        /** A tap on a trial-ended offer popup (Master → Activation → Dashboard). Never throws to the caller. */
        async trialOfferClick(click: {offer: 'league' | 'tournament' | 'account'; event_uid?: string; action: string; label?: string}): Promise<{recorded: boolean}> {
            return apiActionImproved('trial-offer/click' as any, click)
        },
        async offerClick(click: {tournament_uid?: string; league_uid?: string; action: 'shown' | 'sample' | 'live_example' | 'buy' | 'register_list' | 'register_copy' | 'plan' | 'payment_setup'; amount_cents?: number}): Promise<{recorded: boolean}> {
            return apiActionImproved('event-pass/offer-click' as any, click)
        },
        async earnStart(event: {tournament_uid?: string; league_uid?: string}): Promise<{started: boolean}> {
            return apiActionImproved('event-pass/earn-start' as any, event)
        },
        async earnClaim(event: {tournament_uid?: string; league_uid?: string}): Promise<{granted: boolean}> {
            return apiActionImproved('event-pass/earn-claim' as any, event)
        },
        /** The organizer's events behind the trial-ended lock, each with its pass and price (EventPassPicker). */
        async lockedEvents(): Promise<{events: {type: 'tournament' | 'league'; uid: string; title: string; kind: string; amount_cents: number}[]; currency: string}> {
            return apiActionImproved('event-pass/locked-events' as any, {})
        },
        /** One checkout for several locked events; each gets its pass on payment. */
        async checkoutEvents(eventUids: string[], return_path: string): Promise<{redirect_url: string}> {
            return apiActionImproved('event-pass/checkout-events' as any, {events: eventUids, return_path})
        },
        /** The trial-expiry offer for one event the organizer runs (TrialEndedLock, beyond the listed few). */
        async offer(event: {tournament_uid?: string; league_uid?: string}): Promise<any> {
            return apiActionImproved('event-pass/offer' as any, event)
        },
        async apply(event: {tournament_uid?: string; league_uid?: string; surface?: 'banner' | 'modal'}): Promise<{applied: boolean; available: number; topup_cents?: number; pass_tier?: number | null}> {
            return apiActionImproved('event-pass/apply' as any, event)
        },
        /** A gate that rendered or refused on the client (the trial lock, the go-live modal, a share/print refusal). Never throws to the caller's flow. */
        async gateImpression(data: {gate: 'trial_locked' | 'go_live' | 'feature_locked'; code?: string; surface?: string; tournament_uid?: string; league_uid?: string; entries?: number; unit?: string; offer_shown?: string; action_taken?: string}): Promise<{recorded: boolean}> {
            return apiActionImproved('gate-impressions' as any, data)
        },
    },
    organizerPath: {
        /** Move a club between organizer paths. Organizers may go one_off → community; back is master-only. */
        async set(club_uid: string, path: 'one_off' | 'community'): Promise<{success: boolean; club_uid: string; organizer_path: string}> {
            return apiActionImproved('club/organizer-path' as any, {club_uid, path})
        },
    },
    payouts: {
        /**
         * The Stripe payout onboarding link (same endpoint the Referrals page uses). Same-tab
         * redirect on the caller's side: window.open after an await is blocked as a popup.
         */
        async onboardingUrl(return_url: string, payments_bank_country?: string): Promise<{redirect_url?: string; error?: string; collect_on_the_day?: boolean; express_offered?: boolean; account_type?: string}> {
            return apiRetrieveImproved('referral-payout-account-url', {return_url, payments_bank_country} as any)
        },
    },
    discord: {
        async status(): Promise<{linked: boolean; username: string | null; avatar: string | null; linked_at: string | null; dm_opted_out: boolean}> {
            return apiRetrieveImproved('discord/status' as any, {}, {method: 'GET', body: null})
        },
        // The link is the same OAuth round trip as sign-in; the state carries the viewer's uid.
        async linkUrl(): Promise<{url: string}> {
            return apiActionImproved('socialite/discord/url' as any, {mode: 'link'})
        },
        async unlink(): Promise<{success: boolean; linked: boolean; username: string | null; avatar: string | null; linked_at: string | null; dm_opted_out: boolean}> {
            return apiActionImproved('discord/unlink' as any, {})
        },
        async dmOptOut(opted_out: boolean): Promise<{success: boolean; linked: boolean; username: string | null; avatar: string | null; linked_at: string | null; dm_opted_out: boolean}> {
            return apiActionImproved('discord/dm-opt-out' as any, {opted_out})
        },
    },
    dupr: {
        async loginUrl(): Promise<{enabled: boolean; url?: string; origin?: string}> {
            return apiActionImproved('dupr/login-url' as any, {})
        },
        async link(payload: {dupr_id: string; user_token: string; refresh_token?: string; stats?: any}): Promise<{success: boolean; dupr_id: string; stats: any}> {
            return apiActionImproved('dupr/link' as any, payload)
        },
        async unlink(): Promise<{success: boolean}> {
            return apiActionImproved('dupr/unlink' as any, {})
        },
        async refreshRating(): Promise<{success: boolean; dupr_doubles?: string | null; dupr_singles?: string | null}> {
            // debug asks for raw entitlement diagnostics — served on non-production only.
            return apiActionImproved('dupr/refresh-rating' as any, {debug: 1})
        },
        async status(): Promise<{enabled: boolean; linked: boolean; dupr_id: string | null; dupr_doubles?: string | null; dupr_singles?: string | null; stats: any}> {
            return apiRetrieveImproved('dupr/status' as any, {})
        },
        async submitTournament(tournament_uid: string): Promise<{submitted: number; skipped_no_dupr: number; skip_reasons?: string[]; error_count: number; errors: string[]}> {
            return apiActionImproved('dupr/submit-tournament' as any, {tournament_uid})
        },
        async setAutoSubmit(tournament_uid: string, enabled: boolean): Promise<{dupr_auto_submit: boolean}> {
            return apiActionImproved('dupr/set-auto-submit' as any, {tournament_uid, enabled})
        },
        async setPremiumOnly(tournament_uid: string, enabled: boolean): Promise<{dupr_premium_only: boolean}> {
            return apiActionImproved('dupr/set-premium-only' as any, {tournament_uid, enabled})
        },
        async clubs(fresh?: boolean): Promise<{linked: boolean; clubs: {clubId: number; clubName: string; role: string}[]; error?: string}> {
            // fresh also asks for debug diagnostics (raw DUPR response) — served on non-production only.
            return apiActionImproved('dupr/clubs' as any, fresh ? {fresh: 1, debug: 1} : {})
        },
        async setClub(tournament_uid: string, club_id: number | null): Promise<{dupr_club_id: number | null; dupr_club_name: string | null; venue_only: boolean}> {
            return apiActionImproved('dupr/set-club' as any, {tournament_uid, club_id})
        },
        async registerWebhook(): Promise<{success: boolean; url: string; result?: any; error?: string}> {
            return apiActionImproved('dupr/register-webhook' as any, {})
        },
        async submittedMatches(tournament_uid: string): Promise<{matches: {game_uid: string; team_a: string; team_b: string; score: string; submitted_at: string | null; excluded: boolean}[]}> {
            return apiActionImproved('dupr/submitted-matches' as any, {tournament_uid})
        },
        async deleteMatch(tournament_uid: string, game_uid: string): Promise<{ok: boolean}> {
            return apiActionImproved('dupr/delete-match' as any, {tournament_uid, game_uid})
        },
        async restoreMatch(tournament_uid: string, game_uid: string): Promise<{ok: boolean}> {
            return apiActionImproved('dupr/restore-match' as any, {tournament_uid, game_uid})
        },
    },
};

export default services;

// ---- session sign-ups by WhatsApp ------------------------------------------------------------------

export type SessionValues = {
    title: string;
    session_type: 'league' | 'social' | 'coaching' | 'other';
    starts_at: string;
    ends_at: string | null;
    venue: string | null;
    capacity: number | null;
    pairing_mode: 'fixed' | 'rotating' | 'none';
    signup_cutoff_at: string | null;
    timezone: string;
    // The league whose standings this session feeds, if it is one of the club's.
    league_uid?: string | null;
    // Next week's copy is made when the weekly post goes.
    repeat_weekly?: boolean;
    // Courts for its games; empty for one court per four places.
    courts?: number | null;
    // The format: racquet (courts, pairs, a score per game) or a team game (a squad, teams picked
    // on the day, one final score). A new session takes the club's sport's default.
    format?: SessionFormat;
    // A team game's setup: how many teams, what they are called, how they are picked, and
    // whether the organizer is asked who scored.
    team_count?: number;
    team_names?: string[];
    team_mode?: 'manual' | 'balanced';
    ask_scorers?: boolean;
    // What a place costs, in the club's currency; empty for a session that is free.
    price?: number | null;
    // When the card is charged, and whether someone who drops out after that still pays.
    charge_cutoff_hours?: number;
    charge_late_drops?: boolean;
    // Who pays ScorePal's fee on it, as on an entry fee. Absorb is the organizer.
    fee_split_mode?: 'absorb' | 'pass' | 'split_fixed' | 'split_percent';
};

/** What a name on a session has paid, held or been given back. */
export type SessionPaymentSummary = {
    uid: string;
    status: 'pending' | 'card_saved' | 'held' | 'charged' | 'failed' | 'released' | 'refunded';
    amount: number;
    // The part of it that is ScorePal's fee, where the session passes it on.
    fee: number;
    refunded: number;
    // What is left of what was paid, that could still be refunded.
    refundable: number;
    // Asked of Stripe, and not yet done.
    refund_requested: boolean;
    currency: string;
    label: string;
};

/** Whether this club's sessions can have a price, and if not, why not. */
export type SessionPaymentsAvailable = {enabled: boolean; reason: string | null; currency: string};

/** How a club runs its week. Times are by the club's clock; post_day is 1 Monday … 7 Sunday. */
export type SessionWeekSettings = {
    post_day: number;
    post_time: string;
    reminder_hours: number;
    cutoff_hours: number | null;
    courts: number | null;
    league_uid: string | null;
    // What a repeating session costs unless it says otherwise, and how it is charged.
    price: number | null;
    charge_cutoff_hours: number;
    charge_late_drops: boolean;
    fee_split_mode: 'absorb' | 'pass' | 'split_fixed' | 'split_percent';
    // The results of the club's leagues and tournaments, to its organizers on WhatsApp.
    results_digest: 'off' | 'daily' | 'immediate';
    // When a session's game is scored on WhatsApp, ask who played left and who played right.
    ask_sides: boolean;
};

/** One of the club's players, found by name to add to a session. */
export type SessionPlayerHit = {uid: string; name: string; detail: string | null};

export type SessionGameSummary = {uid: string; round: number; court: number; matchup: string; finished: boolean; score?: string | null};

/** A name from a pasted list, with what was written beside it ("late"). */
export type ListedName = {name: string; note?: string};

/** How the organizer wrote a session's heading: its lines with placeholders, and how they write a time. */
export type SessionHeading = {lines: string[]; time: Record<string, unknown>};

/** How a league writes its own post, learnt from a list its organizer pasted. */
export type PostLayout = {heading: string[]; time: Record<string, unknown>; list: string};

/** The form's fields that a reading can flag: worked out or assumed, not written. */
export type SessionField = 'title' | 'session_type' | 'starts_at' | 'ends_at' | 'venue' | 'capacity' | 'pairing_mode' | 'names';

export type ParsedSession = SessionValues & {
    type_label: string | null;
    heading: SessionHeading | null;
    names: ListedName[];
    waitlist: string[];
    flags: Partial<Record<SessionField, string[]>>;
    // The session the club already has at this start and venue: saving changes that one.
    existing?: {uid: string; title: string; when: string; in: number} | null;
};

export type SessionName = {name: string; note?: string; player_uid?: string; source?: 'whatsapp' | 'web' | 'organizer'; verified?: boolean; reach?: 'verified' | 'unverified' | 'placeholder' | 'none'; reminded?: boolean; attending?: boolean; payment?: SessionPaymentSummary};

export type SessionSummary = SessionValues & {
    uid: string;
    version: number;
    type_label: string | null;
    when: string;
    status: 'open' | 'locked' | 'cancelled' | 'completed';
    open: boolean;
    in: SessionName[];
    waitlist: SessionName[];
    left: number | null;
    // "AED 50", and the currency it is in: nothing for a session that is free.
    price_label: string | null;
    currency: string | null;
    // What a place costs the player in all, with whatever of the fee is theirs.
    total_label: string | null;
    // The pairs for its games, as player uids, and the games once they are made.
    pairs: string[][] | null;
    games_made: boolean;
    games: SessionGameSummary[];
    // A team game: its setup, the teams once picked, and the final score once in.
    format: SessionFormat;
    team_count: number | null;
    team_names: string[] | null;
    team_mode: 'manual' | 'balanced' | null;
    ask_scorers: boolean | null;
    teams_picked: boolean | null;
    teams: SessionTeam[] | null;
    result: SessionTeamResult | null;
};

export type SessionFormat = 'racquet' | 'team_game';

export type SessionTeam = {name: string; players: {uid: string; name: string}[]};

/** A team game's final score, the first team's goals first, and who scored as written. */
export type SessionTeamResult = {
    score: [number, number];
    line: string;
    scorers: {uid: string | null; name: string; goals: number}[];
};

/** Squad sizes a team game is usually played at. */
export const SQUAD_PRESETS: {size: number; label: string}[] = [
    {size: 10, label: '10 (5-a-side)'}, {size: 12, label: '12 (6-a-side)'}, {size: 14, label: '14 (7-a-side)'}, {size: 22, label: '22 (11-a-side)'},
];

/**
 * A sign-up code's links: whatsapp_url opens the chat with the message typed; web_url is the page
 * (scorepal.co/s/<CODE>), which WhatsApp shows in a group with `title` (or `default_title`) and
 * the club's logo, and which opens the chat itself on a phone. The post carries web_url.
 */
export type SignupLink = {code: string; whatsapp_url: string; web_url: string; message: string; title: string | null; default_title: string};

/** An event's registration by WhatsApp: whether it is open (and why not), its code, and the post. */
export type EventRegistrationLink = {
    event: {uid: string; type: 'league' | 'tournament'; title: string; slug: string | null; url: string; entry_starts_at?: string | null; entry_ends_at?: string | null};
    registration: {
        open: boolean;
        phase: string;
        full: boolean;
        // Why it is closed, for a player; and for the organizer, what would open it.
        reason: string | null;
        fix: string | null;
        // False when the event asks questions, shows a waiver or is entered as a team: web only.
        in_chat: boolean;
        web_only: string | null;
    };
    link: SignupLink | null;
    post: string | null;
    page_url: string;
};

/** What /s/<CODE> shows: a club's sessions, or an event's registration. */
export type RegistrationPage = (
    | {scope: 'club'; club: {uid: string; name: string; logo_url?: string | null}; sessions: SessionSummary[]; link: SignupLink}
    | ({scope: 'event'} & Omit<EventRegistrationLink, 'link'> & {link: SignupLink})
) & {
    // What WhatsApp shows for the page's link in a group: from the page's own tags.
    share: {title: string; description: string; image: string | null};
};

export type ClubSessionsOverview = {
    enabled: boolean;
    // The club, its sport, and the format a new session takes from it.
    club: {uid: string; name: string; timezone: string | null; sport: string | null; default_format: SessionFormat};
    // The club's own leagues, for a session that feeds one.
    leagues: {uid: string; title: string}[];
    sessions: SessionSummary[];
    // The last week's sessions, newest first: their games and results.
    recent: SessionSummary[];
    // Where the club stands with the WhatsApp Sessions trial (null with its switch off): paid, on
    // its free session (`trial` names it once published), or locked at publishing — and why.
    access: {
        mode: 'paid' | 'trial' | 'locked';
        reason: 'used' | 'no_phone' | null;
        capacity: number | null;
        trial: {started_at: string | null; session_uid: string | null; session_title: string | null; session_at: string | null; capacity: number} | null;
    } | null;
    link: SignupLink | null;
    // Null while publishing is locked.
    post: string | null;
    // Whose layout the post is in: the league's own, learnt from a pasted list, or ScorePal's.
    layout: 'own' | 'scorepal';
    // The number players message: the same for every club. Null where none is set.
    whatsapp_number: {e164: string; display: string} | null;
    settings: SessionWeekSettings;
    payments: SessionPaymentsAvailable;
};
